import { DOCUMENT_TAB, isFocusedSessionArtifact } from './sessionArtifactFocus';
import type { Artifact } from './state';

const STORAGE_KEY_PREFIX = 'heidi-work:last-open-artifact:v1:';
// Which routine a chat belongs to. Separate from the card record above: hiding
// the card deletes that record, and the header still needs the routine after
// the chat has aged out of the newest runs list.
const ROUTINE_FOR_PROJECT_KEY_PREFIX = 'heidi-work:routine-for-project:v1:';

/**
 * Durable id-only artifacts safe to rehydrate after refresh / project return.
 *
 * Excludes ephemeral / unsafe payloads:
 *   - ``document-export`` — signed download URLs
 *   - ``run-attachment`` — presigned URL minted at preview time
 *   - ``created-objects`` — in-memory snapshot / display rows
 *   - ``session-list`` / ``note-push-verification`` — transient UI surfaces
 */
type SessionArtifact = Extract<Artifact, { type: 'session' }>;

type PersistedArtifact =
  | SessionArtifact
  | Extract<Artifact, { type: 'patient' }>
  | Extract<Artifact, { type: 'note' }>
  | Extract<Artifact, { type: 'tasks' }>
  | Extract<Artifact, { type: 'codes' }>
  | Extract<Artifact, { type: 'letter' }>
  | Extract<Artifact, { type: 'document' }>
  | Extract<Artifact, { type: 'template-builder' }>
  | Extract<Artifact, { type: 'form-template' }>
  | Extract<Artifact, { type: 'presentation' }>
  | Extract<Artifact, { type: 'memory' }>
  | Extract<Artifact, { type: 'workflow' }>;

const storageKey = (projectId: string) => `${STORAGE_KEY_PREFIX}${projectId}`;
const routineForProjectKey = (projectId: string) =>
  `${ROUTINE_FOR_PROJECT_KEY_PREFIX}${projectId}`;

const hasString = (value: Record<string, unknown>, key: string): boolean =>
  typeof value[key] === 'string' && value[key].length > 0;

/**
 * Runtime-validate unknown JSON into a persistable artifact identity.
 *
 * Work restores its canvas from this record after a refresh. Preserve only the
 * focused V2 session targets (`isFocusedSessionArtifact`: Context, Transcript,
 * Note, and a fully identified Document); all other tab intent remains
 * ephemeral and falls back to the session panel default. Whether a GENERIC
 * session may be restored at all is the host's (flag-aware) decision, not this
 * module's.
 */
export const toPersistedArtifact = (value: unknown): PersistedArtifact | null => {
  if (!value || typeof value !== 'object') return null;

  const candidate = value as Record<string, unknown>;
  switch (candidate.type) {
    case 'session': {
      if (!hasString(candidate, 'sessionId')) return null;

      const session: SessionArtifact = {
        type: 'session',
        sessionId: candidate.sessionId as string,
      };

      const withFocus: SessionArtifact = {
        ...session,
        ...(typeof candidate.focusTab === 'string'
          ? { focusTab: candidate.focusTab as SessionArtifact['focusTab'] }
          : {}),
        ...(hasString(candidate, 'focusDocumentId')
          ? { focusDocumentId: candidate.focusDocumentId as string }
          : {}),
      };
      if (!isFocusedSessionArtifact(withFocus)) return session;
      // A Context/Transcript/Note focus never carries a document id.
      return withFocus.focusTab === DOCUMENT_TAB
        ? withFocus
        : { ...session, focusTab: withFocus.focusTab };
    }
    case 'patient':
      return hasString(candidate, 'patientId')
        ? { type: 'patient', patientId: candidate.patientId as string }
        : null;
    case 'note':
    case 'tasks':
    case 'codes':
      return hasString(candidate, 'sessionId')
        ? {
            type: candidate.type,
            sessionId: candidate.sessionId as string,
          }
        : null;
    case 'document':
      return hasString(candidate, 'sessionId') && hasString(candidate, 'documentId')
        ? {
            type: 'document',
            sessionId: candidate.sessionId as string,
            documentId: candidate.documentId as string,
          }
        : null;
    case 'letter':
      return hasString(candidate, 'sessionId') && hasString(candidate, 'letterId')
        ? {
            type: 'letter',
            sessionId: candidate.sessionId as string,
            letterId: candidate.letterId as string,
          }
        : null;
    case 'template-builder':
    case 'form-template':
      return hasString(candidate, 'templateId')
        ? {
            type: candidate.type,
            templateId: candidate.templateId as string,
          }
        : null;
    case 'presentation':
      return hasString(candidate, 'presentationId')
        ? {
            type: 'presentation',
            presentationId: candidate.presentationId as string,
          }
        : null;
    case 'memory':
      return hasString(candidate, 'memoryId')
        ? { type: 'memory', memoryId: candidate.memoryId as string }
        : null;
    case 'workflow':
    case 'workflow-config':
      // Legacy sessionStorage may still hold ``workflow-config`` from before
      // AGE-1799 collapsed both panels onto ``workflow`` — normalize on read.
      return hasString(candidate, 'workflowId')
        ? { type: 'workflow', workflowId: candidate.workflowId as string }
        : null;
    default:
      return null;
  }
};

export const readProjectArtifact = (projectId: string): Artifact | null => {
  if (typeof globalThis.sessionStorage === 'undefined') return null;

  try {
    const serialized = globalThis.sessionStorage.getItem(storageKey(projectId));
    if (!serialized) return null;
    return toPersistedArtifact(JSON.parse(serialized));
  } catch {
    return null;
  }
};

/**
 * Drop a project's stored artifact ONLY when it is a routine configuration.
 *
 * Navigating into a routine run from the sidebar (or hopping between runs)
 * lands on the chat, not the configuration — so the persisted ``workflow``
 * panel must not restore over it. A session / patient / document artifact the
 * clinician left open there is unrelated to that decision and still restores.
 */
export const clearProjectWorkflowArtifact = (projectId: string): void => {
  if (typeof globalThis.sessionStorage === 'undefined') return;

  try {
    if (readProjectArtifact(projectId)?.type !== 'workflow') return;
    globalThis.sessionStorage.removeItem(storageKey(projectId));
  } catch {
    // Storage may be unavailable; the in-memory canvas still works.
  }
};

/** The routine behind this chat, remembered after a landing or a runs-list match. */
export const readRoutineForProject = (projectId: string): string | null => {
  if (typeof globalThis.sessionStorage === 'undefined') return null;

  try {
    const workflowId = globalThis.sessionStorage.getItem(routineForProjectKey(projectId));
    return workflowId && workflowId.length > 0 ? workflowId : null;
  } catch {
    return null;
  }
};

/** Remember the pair. Hiding the routine card must not remove it. */
export const writeRoutineForProject = (projectId: string, workflowId: string): void => {
  if (typeof globalThis.sessionStorage === 'undefined') return;
  if (!projectId || !workflowId) return;

  try {
    globalThis.sessionStorage.setItem(routineForProjectKey(projectId), workflowId);
  } catch {
    // Storage may be unavailable or full; the runs list can still resolve the chat.
  }
};

export const writeProjectArtifact = (
  projectId: string,
  artifact: Artifact | null
): void => {
  if (typeof globalThis.sessionStorage === 'undefined') return;

  try {
    const persisted = toPersistedArtifact(artifact);
    if (persisted) {
      globalThis.sessionStorage.setItem(storageKey(projectId), JSON.stringify(persisted));
    } else {
      globalThis.sessionStorage.removeItem(storageKey(projectId));
    }
  } catch {
    // Storage may be unavailable or full; the in-memory canvas still works.
  }
};
